<script setup>
import { ref, onMounted, onUnmounted, computed, watch, nextTick, defineEmits } from 'vue';
import axios from 'axios';
import { useRoute, useRouter } from 'vue-router';
import { useLayoutComponents } from '@/composables/layout';
import { useStorage } from '@/composables/use-storage';
import {
  hasProposalAccepted,
  userHasProposalToListing,
  fetchChatHistory,
} from "@/services/api";
import { fetchTaskList } from '@/services/evalService';
import BidModal from '@/components/BidModal.vue';
import RFPRespondModal from '@/components/modals/RFPRespondModal.vue';
import RequestAccessDatasetModal from '@/components/modals/RequestAccessDatasetModal.vue';
import SchemaTable from '@/components/SchemaTable.vue';
import CheckoutModal from '@/components/modals/CheckoutModal.vue';
import SubmitProposalModal from '@/pages/user/proposal/submitProposalModal.vue';
import RunEvalModal from '@/components/modals/RunEvalModal.vue';
import TaskBundles from '@/components/TaskBundles.vue';
import AuthPromptModal from '@/components/modals/AuthPromptModal.vue';
import ProfilePopup from '@/pages/explore/components/ProfilePopup.vue';
import { useChat } from "@/composables/use-chat";
import { useToast } from '@/composables/use-toast';
import { fetchListingLicense } from '@/services/licenseService.js'
import EvalLeaderboards from '@/pages/eval/Leaderboards.vue';

const { Tabs, MarkdownRenderer } = useLayoutComponents();

const route = useRoute();
const router = useRouter();

const emit = defineEmits(['leaderboard-data']);

const {
  hasPermission,
  hasFlag,
  isLoggedIn,
  getUserInfo,
  clearChatQuestionsAndAnswersCache,
} = useStorage();

const { initializeChat, getChatQuestionsAndAnswers, disconnectWebSocket } =
  useChat();

const userInfo = ref(getUserInfo());
const isAdmin = userInfo.value?.is_admin === true;

const showCreatorBanner = computed(
  () => isLoggedIn() && !hasPermission('datasets') && item.value?.eval_price > 0
);

const qaError = ref(false);
const qaResponseReceived = ref(false);

// Chat methods
const getChatQuestionsAndAnswersFromServer = async (forceRefresh = false) => {
  try {
    const id = route.params.id;
    if (!id) return;

    const response = await getChatQuestionsAndAnswers(forceRefresh, id);

    qaError.value = !!response.error;
    conversationStarters.value = response?.chatQuestions || [];
  } catch (error) {
    console.error("Error fetching chat questions and answers:", error);
  } finally {
    qaResponseReceived.value = true;
  }
};
const { addToast } = useToast();
const item = ref(null);

const datasets = ref(null);
const minimumBid = ref(null);
const itemImage = ref(null);
const showCheckoutModal = ref(false);
const isBidActive = ref(false);
const buyActive = ref(null);
const bidActive = ref(null);
const isRFPRespondActive = ref(false);
const rfpRespond = ref(null);
const isStripeActive = ref(false);
const buyNowClicked = ref(false);
const bidClicked = ref(false);
const rfpRespondClicked = ref(false);
const showSetUserCountry = ref(false);
const isLoading = ref(true);
const expireAt = ref(null);
const showRequestAccessDatasetModal = ref(false);
const showRunEvalModal = ref(false);
const showAuthPromptModal = ref(false);
const authPromptAction = ref('');
const isLoadingTaskBundles = ref(false);
const taskBundlesAccessDenied = ref(false);
const showProfilePopup = ref(false);

const profileData = computed(() => {
  const sellerUser = item.value?.datasets[0]?.dataset.seller?.user;
  const isVerified = sellerUser?.seller_whitelist === true;
  return {
    avatar: sellerUser?.profile_pic,
    name: sellerUser?.username,
    verified: isVerified,
    communityCreator: !isVerified && !!item.value?.datasets[0]?.dataset.seller,
    title: sellerUser?.profile_info,
    username: sellerUser?.username?.toLowerCase(),
    credentials: sellerUser?.badges,
    socials: sellerUser?.socials
  };
});

const isAnyModalActive = computed(() => {
  return (
    showCheckoutModal.value ||
    isBidActive.value ||
    isRFPRespondActive.value ||
    isStripeActive.value ||
    showSetUserCountry.value ||
    showRequestAccessDatasetModal.value ||
    showRunEvalModal.value ||
    showAuthPromptModal.value ||
    showProfilePopup.value ||
    isLoading.value
  );
});

const isRightSectionLoading = computed(() => {
  return isLoading.value || (hasFlag('asymmetry0-chat') && !qaError.value && !qaResponseReceived.value);
});

const tabs = computed(() => {
  const baseTabs = [
    { label: "Card", value: "card", id: "card" },
    { label: "License", value: "license", id: "license" },
  ];

  if (item.value?.eval_price > 0) {
    baseTabs.splice(1, 0, { label: "Task Bundles", value: "task-bundles", id: "task-bundles" });
    baseTabs.splice(2, 0, { label: "Leaderboards", value: "leaderboards", id: "leaderboards" });
  } else if (item.value?.is_open_source === true) {
    baseTabs.splice(1, 0, { label: "Leaderboards", value: "leaderboards", id: "leaderboards" });
  } else if (item.value?.datasets?.length) {
    baseTabs.splice(1, 0, { label: "Schema", value: "schema", id: "schema" });
  }

  return baseTabs;
});

const fetchItemDetails = async () => {
  try {
    isLoading.value = true;
    hasLeaderboardData.value = false;
    leaderboardData.value = [];
    const id = route.params.id;
    const response = await axios.get(`/api/listings/${id}`);
    if (response.data?.id) {
      item.value = response.data;
      datasets.value = item.value.datasets[0];
      minimumBid.value = item.value.minimum_bid;
      expireAt.value = item.value.expire_at;

      if (Array.isArray(item.value.schema)) {
        item.value.schema = {
          fields: item.value.schema.map((field) => ({
            name: field.name,
            type: field.type,
            description: field.description,
          })),
        };
      }

            itemImage.value = item.value.preview_image ?? null;
        } else {
            addToast('This listing does not exist or not available', 'error');
            router.push('/explore');
        }
    } catch (error) {
        console.error('Error fetching item:', error);
    } finally {
        isLoading.value = false;
    }
}
const activeTab = ref('card');
const loadingTab = ref(false);
const baseLicenseContent = ref('');
const licenseContent = ref('');
const yearsDuration = ref(1);
const customClauses = ref([]);
const evalRunProcessFinished = ref(false);
const tasksBundleFile = ref(null);
const leaderboardData = ref([]);
const hasLeaderboardData = ref(false);

const handleLeaderboardLoaded = (rows) => {
  leaderboardData.value = Array.isArray(rows) ? rows : [];
  hasLeaderboardData.value = true;
};

const convertCustomClausesToMarkdown = () => {
    let result = '';
    customClauses.value.forEach(clause => {
        result += `## ${clause.name}\n\n${clause.content}\n\n`;
    });
    return result;
}

const updateLicenseContent = () => {
    if (!baseLicenseContent.value) return;
    
    const additionalClauses = convertCustomClausesToMarkdown();
    
    let content = baseLicenseContent.value;
    content = content.replace('{{duration}}', `${yearsDuration.value} year${yearsDuration.value > 1 ? 's' : ''}`);
    content = content.replace('{{additional_clauses}}', additionalClauses);
    
    licenseContent.value = content;
}

const handleTabChange = async (tab) => {
    activeTab.value = tab;
    
    if (activeTab.value === 'license' && !baseLicenseContent.value) {
        if (!item.value?.id) {
            addToast('Listing license not found', 'error');
            return;
        }
        
        try {
            loadingTab.value = true;
            const licenseData = await fetchListingLicense(item.value.id);
            
            if (licenseData?.license) {
                baseLicenseContent.value = licenseData.license.content;
                yearsDuration.value = licenseData.years_duration || 1;
                customClauses.value = licenseData.custom_clauses || [];
                
                updateLicenseContent();
            }
        } catch (error) {
            console.error('Error loading license:', error);
        } finally {
            loadingTab.value = false;
        }
    }
};

onMounted(async () => {
  await fetchItemDetails();
  if (item.value?.id) {
    await checkCanRespondToRfp();
    await checkCanSubmitProposal();
  }
  if (hasFlag("asymmetry0-chat")) {
    initializeChat(route.params.id);
    await getChatQuestionsAndAnswersFromServer(true);
  }
  const hasTaskBundles = item.value?.datasets?.[0]?.dataset?.eval?.total_tasks > 0;
  const datasetId = parseInt(item.value?.datasets?.[0]?.dataset_id, 10);

  if (hasTaskBundles && !isLoggedIn()) {
    taskBundlesAccessDenied.value = true;
  } else if (hasTaskBundles && Number.isInteger(datasetId)) {
    try {
      isLoadingTaskBundles.value = true;
      const taskListFile = await fetchTaskList(datasetId);
      if (taskListFile?.accessDenied) {
        taskBundlesAccessDenied.value = true;
      } else if (taskListFile) {
        tasksBundleFile.value = taskListFile;
        item.value.task_bundles = taskListFile;
      }
    } catch (error) {
      console.error('Error fetching task list:', error);
    } finally {
      isLoadingTaskBundles.value = false;
    }
  }
});

const isExpired = computed(() => {
  if (!item.value?.expire_at) return false;
  if (item.value?.applicant?.id && item.value?.datasets?.length > 0)
    return false;
  const expirationDate = new Date(item.value.expire_at);
  return expirationDate < new Date();
});

function toggleBuyNowModal(selected = null) {
  if (isExpired.value) return;
  if (isAnyModalActive.value) return;
  showCheckoutModal.value = !showCheckoutModal.value;
  buyActive.value = selected;
}

function toggleBidModal(selected = null) {
  if (isExpired.value) return;
  if (isAnyModalActive.value) return;
  isBidActive.value = !isBidActive.value;
  bidActive.value = selected;
}

function toggleRFPRespondModal(selected = null) {
  if (isExpired.value) return;
  if (isAnyModalActive.value) return;
  isRFPRespondActive.value = !isRFPRespondActive.value;
  rfpRespond.value = selected;
}

function promptAuth(action) {
  authPromptAction.value = action;
  showAuthPromptModal.value = true;
}

function handleAuthLogin() {
  router.push('/login');
}

function handleAuthRegister() {
  router.push({ path: '/register', query: { onboarding: 'buyer' } });
}

function handleBuyNowClick(selected = null) {
  if (!isLoggedIn()) {
    promptAuth('purchase this dataset');
    return;
  }
  toggleBuyNowModal(selected);
}

function handleBidClick(selected = null) {
  if (!isLoggedIn()) {
    promptAuth('place a bid');
    return;
  }
  toggleBidModal(selected);
}

function handleRunEvalClick() {
  if (!isLoggedIn()) {
    promptAuth('run this eval');
    return;
  }
  showRunEvalModal.value = true;
}

function handleTaskBundlesUnlock() {
  if (!isLoggedIn()) {
    promptAuth('view task bundles');
    return;
  }
  if (item.value?.price > 0) {
    handleBuyNowClick(item.value);
    return;
  }
  if (item.value?.eval_price > 0) {
    handleRunEvalClick();
  }
}

function handleSubmitProposalClick() {
  if (!isLoggedIn()) {
    promptAuth('submit a proposal');
    return;
  }
  if(!hasPermission('datasets')) {
    addToast('You do not have permission to submit a proposal.', 'error');
    return;
  }
  submitProposalModal.value = true;
}

function getTagStyle(tag) {
  return {
    backgroundColor: `${tag.display_color}25`,
    color: tag.display_color,
  };
}

function formatFileSize(size) {
  if (!size || size === null || size === undefined) return null;
  
  const numSize = Number(size);
  if (isNaN(numSize) || numSize <= 0) return null;
  
  const units = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.floor(Math.log(numSize) / Math.log(1024));
  return (numSize / Math.pow(1024, index)).toFixed(2) + " " + units[index];
}

function formatDate(date) {
  if (date === null) return "N/A";
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatPrice(price) {
  const numPrice = Number(price);

  if (Number.isInteger(numPrice)) {
    return (
      "$" +
      numPrice.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
    );
  }

  return (
    "$" +
    numPrice.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

const CATEGORY_MAPPING = {
  modality: "Modalities",
  domain: "Domains",
  "use case": "Use Cases",
  other: "Others",
};

const groupedTags = computed(() => {
  if (!item.value?.tags)
    return {
      Modalities: [],
      "Use Cases": [],
      Domains: [],
    };

  return item.value.tags.reduce((acc, tag) => {
    const originalCategory = (tag.category || "other").toLowerCase();
    const mappedCategory = CATEGORY_MAPPING[originalCategory];
    const category = mappedCategory || (tag.category || "other");

    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(tag);

    if (tag.tag_type?.is_eval === true && tag.tag_type?.type) {
      if (!acc["Industry"]) {
        acc["Industry"] = [];
      }
      
      const industryTag = {
        id: `industry-${tag.id}`,
        name: tag.tag_type.type,
        display_color: tag.display_color || "#3B82F6",
        category: "Industry",
        tag_type: tag.tag_type,
        isEvalTag: true
      };
      
      acc["Industry"].push(industryTag);
    }

    return acc;
  }, {});
});

const nonEmptyTagGroups = computed(() => {
  return Object.entries(groupedTags.value)
    .filter(([, tags]) => tags.length > 0)
    .reduce((acc, [category, tags]) => {
      acc[category] = tags;
      return acc;
    }, {});
});

const expandedCategories = ref({});

const conversationStarters = ref([]);

const customQuestion = ref("");
const chatQuestion = ref("");
const chatStarted = ref(false);
const isChatLoading = ref(false);

// Chat variables
const { sendChatQuestion } = useChat();
const messages = ref([]);
const chatHistory = ref([]);
const messagesContainer = ref(null);
const chatInputRef = ref(null);
const sessionId = computed(() => `${userInfo.value.id}:${route.params.id}`);
const model = "gpt-4o-mini";

const handleKeyDown = (event) => {
  if (event.key === "Enter" && !event.shiftKey) {
    event.preventDefault();
    askQuestion();
  }
};

const autoResize = (event) => {
  const textarea = event.target;
  const text = textarea.value;
  const lineHeight = 20;
  const baseHeight = 40;

  const lineBreaks = (text.match(/\n/g) || []).length;

  const calculatedHeight = baseHeight + lineBreaks * lineHeight;
  const maxHeight = baseHeight + 2 * lineHeight;

  if (calculatedHeight <= maxHeight) {
    textarea.style.height = calculatedHeight + "px";
    textarea.style.overflowY = "hidden";
  } else {
    textarea.style.height = maxHeight + "px";
    textarea.style.overflowY = "auto";
  }
};

const handleInput = (event) => {
  autoResize(event);
};

const askQuestion = async () => {
  if (!customQuestion.value.trim() || isChatLoading.value) return;

  const questionText = customQuestion.value;
  customQuestion.value = "";

  nextTick(() => {
    if (chatInputRef.value) {
      chatInputRef.value.style.height = "40px";
    }
  });

  try {
    if (!chatStarted.value) {
      await openConversation(questionText);
    } else {
      await sendMessage(questionText);
    }
  } catch (error) {
    console.error("Error sending question:", error);
    isChatLoading.value = false;
  }
};

const openConversation = async (question) => {
  chatQuestion.value = question;
  chatStarted.value = true;

  await getChatHistory();

  if (question && question.trim()) {
    await sendMessage(question);
  }
};

const sendMessage = async (messageText) => {
  if (!messageText || !messageText.trim()) return;

  messages.value.push({
    type: "user",
    content: messageText,
    timestamp: new Date(),
  });

  chatHistory.value.push({
    role: "user",
    content: messageText,
  });

  const loadingMessageIndex = messages.value.length;
  messages.value.push({
    type: "assistant",
    content: "",
    loading: true,
    timestamp: new Date(),
  });

  isChatLoading.value = true;

  await nextTick();
  scrollToBottom();

  try {
    const response = await sendChatQuestion(
      messageText,
      route.params.id,
      sessionId.value,
      model,
      chatHistory.value
    );

    if (loadingMessageIndex < messages.value.length) {
      messages.value[loadingMessageIndex] = {
        type: "assistant",
        content:
          response ||
          "Sorry, there was an error processing your question. Please try again.",
        loading: false,
        timestamp: new Date(),
      };

      if (response) {
        chatHistory.value.push({
          role: "assistant",
          content: response,
        });
      }
    }
  } catch (error) {
    console.error("Error sending message:", error);

    if (loadingMessageIndex < messages.value.length) {
      messages.value[loadingMessageIndex] = {
        type: "assistant",
        content:
          "Sorry, there was an error processing your question. Please try again.",
        loading: false,
        timestamp: new Date(),
      };
    }
  } finally {
    isChatLoading.value = false;
    await nextTick();
    scrollToBottom();

    if (chatInputRef.value) {
      chatInputRef.value.focus();
    }
  }
};

const scrollToBottom = () => {
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
};

const getChatHistory = async () => {
  try {
    const history = await fetchChatHistory(sessionId.value);

    if (history && history.length > 0) {
      chatHistory.value = history;
      messages.value = history.map((item) => ({
        type: item.role === "user" ? "user" : "assistant",
        content: item.content,
        loading: false,
        timestamp: new Date(),
      }));
      await nextTick();
      scrollToBottom();
    }
  } catch (error) {
    console.error("Error fetching chat history:", error);
  }
};

const closeChat = () => {
  chatStarted.value = false;
  chatQuestion.value = "";
};

// Handle ESC key to close chat
const handleKeydown = (event) => {
  if (event.key === "Escape" && chatStarted.value) {
    closeChat();
  }
};

watch(chatStarted, (newValue) => {
  if (newValue) {
    document.addEventListener("keydown", handleKeydown);
  } else {
    document.removeEventListener("keydown", handleKeydown);
  }
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
  document.body.style.overflow = "";
  clearChatQuestionsAndAnswersCache();
  disconnectWebSocket();
});

const firstModalityTag = computed(() => {
  const modalityTags = nonEmptyTagGroups.value["Modalities"] || [];
  return modalityTags[0] || null;
});

const firstOccupationTag = computed(() => {
  const occupationTags = nonEmptyTagGroups.value["Occupation"] || [];
  return occupationTags[0] || null;
});

const firstIndustryTag = computed(() => {
  const industryTags = nonEmptyTagGroups.value["Industry"] || [];
  return industryTags[0] || null;
});

const visibleTags = computed(() => {
  return Object.entries(nonEmptyTagGroups.value).reduce(
    (acc, [category, tags]) => {
      const limit = category === "Use Cases" ? 3 : 5;
      acc[category] = expandedCategories.value[category]
        ? tags
        : tags.slice(0, limit);
      return acc;
    },
    {}
  );
});

const hasMoreTags = computed(() => {
  return Object.entries(nonEmptyTagGroups.value).reduce(
    (acc, [category, tags]) => {
      const limit = category === "Use Cases" ? 3 : 5;
      acc[category] = tags.length > limit;
      return acc;
    },
    {}
  );
});

function toggleTagExpansion(category) {
  expandedCategories.value = {
    ...expandedCategories.value,
    [category]: !expandedCategories.value[category],
  };
}

const isDatasetOwner = computed(() => {
  const userInfo = getUserInfo();
  if (!userInfo?.id) return false;
  return userInfo.id === item.value?.datasets[0]?.dataset?.seller?.user?.id;
});

const isRFPOwner = computed(() => {
  const userInfo = getUserInfo();
  if (!userInfo?.id) return false;
  return userInfo.id === item.value?.applicant?.id;
});

const gridColumns = computed(() => {
  let visibleItemsCount = 0;
  
  const isBuySectionVisible = hasPermission('buy') && 
    item.value?.price > 0 &&
    item.value?.datasets?.length;
  
  const isBidSectionVisible = hasPermission('buy') && 
    item.value?.minimum_bid > 0;
  
  if (isBuySectionVisible) visibleItemsCount++;
  if (isBidSectionVisible) visibleItemsCount++;
  
  if (visibleItemsCount === 1 && chatStarted.value) {
    return 'grid-cols-1';
  }
  
  return chatStarted.value ? 'grid-cols-2' : 'grid-cols-1';
});

function urlSlug(str) {
  if (!str || typeof str !== 'string') return '';
  return str
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/(^-)|(-$)/g, '');
}

const itemGithubUrl = computed(() => {
  if (!item.value?.is_open_source) return '';
  const username = item.value?.datasets?.[0]?.dataset?.seller?.user?.username;
  const datasetName = item.value?.name;
  const datasetId = item.value?.datasets?.[0]?.dataset_id;
  if (!username || !datasetName || datasetId == null) return '';
  const slug = urlSlug(datasetName);
  const path = slug ? `${slug}-${datasetId}` : String(datasetId);
  return `https://github.com/portex-ai/tasks/${encodeURIComponent(username)}/${path}`;
});
const hasValidGithubUrl = computed(() => {
  const url = itemGithubUrl.value;
  return url && url.trim() !== '' && url !== '#';
});

const goToEditListing = () => {
  if (isRFPOwner.value) {
    router.push({
      name: "rfp-edit",
      params: { id: item.value.id },
    });
  } else {
    localStorage.setItem("temp_editing_listing_id", item.value.id);
    router.push({
      name: "listing-edit",
    });
  }
};

// RFP Proposal
const canRespondToRfp = ref(false);
const canSubmitProposal = ref(false);
const proposalId = ref(null);

const checkCanRespondToRfp = async () => {
  if (isExpired.value) return false;
  if (!isLoggedIn()) return false;
  const response = await hasProposalAccepted(item.value.id);
  const userInfo = getUserInfo();
  if (!userInfo?.id) return false;
  canRespondToRfp.value = response === Number(userInfo.id);
};

const viewProposal = ref(false);
const checkCanSubmitProposal = async () => {
  if (isExpired.value) return false;
  if (!isLoggedIn()) return false;
  const response = await userHasProposalToListing(item.value.id);
  if (response) {
    canSubmitProposal.value = false;
    proposalId.value = response;
  } else {
    canSubmitProposal.value = true;
  }
  viewProposal.value = true;
};

const submitProposalModal = ref(false);

const redirectToNewProposal = () => {
  router.push({
    name: "creator-profile",
    query: {
      page: "new-proposal",
      rfpId: item.value.id,
    },
  });
};

const goToProposal = () => {
  router.push({
    name: "creator-profile",
    query: {
      page: "new-proposal",
      proposalId: proposalId.value,
    },
  });
};

const goToResponsesRFP = () => {
  router.push({
    name: "creator-profile",
    query: { page: "rfp-responses", listingId: item.value.id },
  });
};
const showCopied = ref(false);

const copyShareUrl = () => {
    const encodedName = encodeURIComponent(item.value.name.replace(/ /g, '-'));
    const shareUrl = `${window.location.origin}/share/listing/${item.value.id}/${encodedName}`;
    navigator.clipboard.writeText(shareUrl);
    showCopied.value = true;
    setTimeout(() => {
        showCopied.value = false;
    }, 2000);
}

const handleEvalCompleted = (evalData) => {
  addToast('Eval run completed successfully!', 'success');
  if(evalData?.sale?.paid) {
    evalRunProcessFinished.value = true;
  }
}

function getProfileImageStyle(badge, index) {
  return `w-12 h-12 rounded-full bg-[#${badge.badge_color}] flex items-center justify-center border-3 border-black/50 relative z-${30 - index*10} -ml-3`;
}

function getBadgeColor(index) {
  const colors = ['bg-violet-600', 'bg-emerald-600', 'bg-sky-600'];
  return colors[index] || 'bg-echoes-primary';
}

function getBadgeInitials(badge) {
  if (!badge.name) return '??';
  
  const name = badge.name.trim();
  
  if (name.includes(' ')) {
    return name
      .split(' ')
      .filter(word => word.length > 0)
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .slice(0, 2);
  }
  
  return name.slice(0, 2).toUpperCase();
}

</script>
<template>
  <main
    class="h-full p-4 pt-8 pb-0 overflow-x-hidden"
  >
    <div class="flex h-full">
      <!-- Left Section -->
      <section
        class="hidden space-y-4 lg:flex w-[20%] rounded-[28px] rounded-b-none bg-gradient-to-t from-transparent to-echoes-background-gray border border-b-0 border-echoes-border-gray p-3 flex-col overflow-y-auto"
      >
        <div class="w-full relative pb-[100%] flex-shrink-0">
          <div
            v-if="itemImage"
            class="absolute inset-[1px] rounded-[21px] overflow-hidden bg-echoes-background-black group"
          >
            <img
              :src="itemImage"
              alt="Dataset"
              class="w-full h-full object-cover"
            />
            <div
              class="absolute inset-0 bg-black bg-opacity-80 opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col items-center justify-start gap-2 z-20"
            >
              <p
                v-if="item?.short_description"
                class="text-xs 2xl:text-sm text-white/80 font-semibold p-3 w-full custom-line-clamp overflow-y-auto text-shadow break-words tracking-wide leading-relaxed"
              >
                {{ item.short_description }}
              </p>
            </div>
          </div>
          <div
            v-else
            class="absolute inset-[1px] flex items-center justify-center bg-echoes-background-black rounded-[21px]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="64"
              height="64"
              fill="none"
              viewBox="0 0 24 24"
              class="text-white/80"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z M3.27 6.96L12 12.01l8.73-5.05 M12 22.08V12"
              />
            </svg>
          </div>
        </div>
        <div class="max-h-[300px] bg-echoes-background-black/50 rounded-2xl p-4 backdrop-blur-sm space-y-6">
          
            <p class="text-white/80 gap-1 flex items-center text-sm font-medium"><span class="text-white">by:</span><span class="text-echoes-primary">{{ profileData.name }}</span></p>
            
          <!-- Logos Section -->
          <!-- Skeleton Loading -->
          <div class="flex items-center" v-if="isLoading">
            <div v-for="index in 3" :key="index" class="w-12 h-12 rounded-full bg-echoes-background-gray animate-pulse border-3 border-black/50 shadow-lg" :style="{ position: 'relative', zIndex: 30 - index * 10 }"></div>
            <div class="ml-2 w-16 h-4 bg-echoes-background-gray rounded animate-pulse"></div>
          </div>
          <!-- Actual Content -->
          <div v-if="profileData?.credentials?.length > 0" class="flex items-center ml-3">
            <div v-for="(badge, index) in profileData.credentials.slice(0, 3)" :key="index" class="w-12 h-12 border-2 border-echoes-border-gray rounded-full flex items-center justify-center  shadow-lg" :class="[getProfileImageStyle(badge, index), badge.badge_image ? '' : (!badge.badge_color ? getBadgeColor(index) : '')]" :style="{ position: 'relative', zIndex: 30 - index * 10, backgroundColor: (!badge.badge_image && badge.badge_color) ? badge.badge_color : undefined }">
              <img :src="badge.badge_image" alt="Profile Badge" class="w-full h-full object-cover rounded-full" v-if="badge.badge_image">
              <span class="text-white font-semibold text-sm" v-else>{{ getBadgeInitials(badge) }}</span>
            </div>
            <div class="text-white/70 text-sm font-medium ml-2" v-if="profileData?.credentials?.length > 3">
            + {{ profileData?.credentials?.length - 3 }} more
            </div>
            <div class="text-white/70 text-sm font-medium ml-2" v-else></div>
          </div>
            
          <!-- Full Profile Button -->
          <div class="px-2">
            <button @click="showProfilePopup = true" class="w-full bg-transparent flex items-center justify-center gap-2 group">
              <span class="text-xs 2xl:text-sm font-semibold tracking-widest text-white group-hover:text-white/80 transition-colors duration-200">FULL PROFILE</span>
              <i class="uil uil-user-square text-echoes-primary group-hover:text-echoes-primary/80 transition-colors duration-200"></i>
            </button>
          </div>
          
        </div>
        <!-- dataset info section -->
        
          <div class="bg-echoes-background-black/50 rounded-2xl p-4 backdrop-blur-sm" v-if="datasets?.dataset?.eval_id">
            <h3 class="text-sm font-semibold text-echoes-primary mb-4 uppercase tracking-wide text-center">
              Eval Details
            </h3>
            <div class="space-y-3">
          

              <!-- number of tasks -->
              <div class="flex flex-col">
                <p class="text-xs text-gray-400 mb-1">Number of Tasks</p>
                <span
                  v-if="isLoading"
                  class="inline-block w-24 h-4 bg-echoes-background-gray rounded animate-pulse"
                ></span>
                <p v-else class="text-sm 2xl:text-base text-white font-medium">
                  {{ datasets?.dataset?.eval?.total_tasks }}
                </p>
              </div>

              <div v-if="firstOccupationTag" class="space-y-3">
                  <!-- Occupation -->
                  <div class="flex flex-col">
                    <p class="text-xs text-gray-400 mb-1 flex items-center gap-1">
                    SOC Occupation 
                    <div>
                      <i class="uil uil-info-circle text-sm cursor-help">
                        <v-tooltip activator="parent" location="top right" interactive>
                          <div class="text-xs">
                            Mapping evals to jobs (based on <a href="https://www.bls.gov/soc/" target="_blank" class="text-echoes-primary">U.S. SOC titles</a>)<br>
                              helps everyone see where AI is strong <br>
                              or weak in real-world work so we can plan training,<br> 
                              tooling, and guardrails effectively.<br>
                          </div>
                        </v-tooltip>
                      </i>
                    </div>
                    </p>
                    <span
                      v-if="isLoading"
                      class="inline-block w-24 h-4 bg-echoes-background-gray rounded animate-pulse"
                    ></span>
                    <v-tooltip
                      v-else-if="firstOccupationTag"
                      :text="firstOccupationTag.name"
                      location="top left"
                    >
                      <template v-slot:activator="{ props }">
                        <span
                          v-bind="props"
                          
                        >
                        <div class="px-2 2xl:px-3 text-xs 2xl:text-sm py-0.5 2xl:py-1 inline-block font-medium rounded-full max-w-[200px]"
                          :style="getTagStyle(firstOccupationTag)"
                          style="display: inline-block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                          {{ firstOccupationTag.name }}

                        </div>
                        </span>
                      </template>
                    </v-tooltip>
                  </div>
    
                  <!-- industry -->
                  <div class="flex flex-col">
                  <p class="text-xs text-gray-400 mb-1 flex items-center gap-1">
                    SOC Industry 
                    <div>
                      <i class="uil uil-info-circle text-sm cursor-help">
                        <v-tooltip activator="parent" location="top right" interactive>
                          <div class="text-xs">
                            Mapping evals to jobs (based on <a href="https://www.bls.gov/soc/" target="_blank" class="text-echoes-primary">U.S. SOC titles</a>)<br>
                              helps everyone see where AI is strong <br>
                              or weak in real-world work so we can plan training,<br> 
                              tooling, and guardrails effectively.<br>
                          </div>
                        </v-tooltip>
                      </i>
                    </div>
                    </p>
                    <span
                      v-if="isLoading"
                      class="inline-block w-24 h-4 bg-echoes-background-gray rounded animate-pulse"
                    ></span>
                    <v-tooltip
                      v-else-if="firstIndustryTag"
                      :text="firstIndustryTag.name"
                      location="top left"
                    >
                      <template v-slot:activator="{ props }">
                        <span
                          v-bind="props"
                        >
                        <div 
                          class="px-2 2xl:px-3 text-xs 2xl:text-sm py-0.5 2xl:py-1 inline-block font-medium rounded-full max-w-[200px]"
                          :style="getTagStyle(firstIndustryTag)"
                          style="display: inline-block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"
                          >

                          {{ firstIndustryTag.name }}
                        </div>
                        </span>
                      </template>
                    </v-tooltip>
                  </div>
                </div>

            </div>
          </div>

          <div class="bg-echoes-background-black/50 rounded-2xl p-4 backdrop-blur-sm" v-if="datasets?.dataset?.file_name">
            <h3 class="text-sm font-semibold text-echoes-primary mb-4 uppercase tracking-wide text-center">
              Core Dataset
            </h3>
            <div class="space-y-3">
          

              <!-- Modality -->
              <div class="flex flex-col">
                <p class="text-xs text-gray-400 mb-1">Modality</p>
                <span
                  v-if="isLoading"
                  class="inline-block w-24 h-4 bg-echoes-background-gray rounded animate-pulse"
                ></span>
                <span
                  v-else-if="firstModalityTag"
                  class="px-2 2xl:px-3 text-xs 2xl:text-sm py-0.5 2xl:py-1 inline-block font-medium rounded-full truncate w-fit"
                  :style="getTagStyle(firstModalityTag)"
                  :title="firstModalityTag.name"
                >
                  {{ firstModalityTag.name }}
                </span>
                <p v-else class="text-sm 2xl:text-base text-white/60">N/A</p>
              </div>

              <!-- Size -->
              <div v-if="datasets && formatFileSize(datasets?.dataset?.file_size)" class="flex flex-col">
                <p class="text-xs text-gray-400 mb-1">Size</p>
                <span
                  v-if="isLoading"
                  class="inline-block w-24 h-4 bg-echoes-background-gray rounded animate-pulse"
                ></span>
                <p v-else class="text-sm 2xl:text-base text-white font-medium">
                  {{ formatFileSize(datasets?.dataset?.file_size) }}
                </p>
              </div>

              <!-- Format -->
              <div class="flex flex-col">
                <p class="text-xs text-gray-400 mb-1">Format</p>
                <span
                  v-if="isLoading"
                  class="inline-block w-24 h-4 bg-echoes-background-gray rounded animate-pulse"
                ></span>
                <p v-else class="text-sm 2xl:text-base text-white font-medium">
                  {{ datasets?.dataset?.external_source ? datasets?.dataset?.external_source.name : `.${datasets?.dataset?.file_extension}` || "N/A" }}
                </p>
              </div>

            

              <!-- Expiration Date -->
              <div v-if="expireAt" class="flex flex-col">
                <p class="text-xs text-gray-400 mb-1">Expiration Date</p>
                <span
                  v-if="isLoading"
                  class="inline-block w-24 h-4 bg-echoes-background-gray rounded animate-pulse"
                ></span>
                <p v-else class="text-sm 2xl:text-base text-red-400 font-medium">
                  {{ formatDate(expireAt) }}
                </p>
              </div>
            </div>
          </div>
      </section>

      <!-- Central Section -->
      <section
        class="w-full flex flex-col h-full transition-all duration-300"
        :class="chatStarted ? 'lg:w-[50%]' : 'lg:w-[60%]'"
      >
        <div class="flex">
          <h1
            class="text-4xl w-full font-semibold mb-3 line-clamp-2 px-4 2xl:px-6 break-words"
          >
            <span
              v-if="isLoading"
              class="inline-block w-3/4 h-9 bg-echoes-background-gray rounded animate-pulse"
            ></span>
            <span v-else>{{ item?.name }}</span>
          </h1>
          <div class="px-2 pt-1">
            <button
              @click="copyShareUrl"
              class="hover:bg-white/20 rounded-md p-1 relative"
            >
              <span class="tooltip">{{
                showCopied ? "Copied!" : "Copy Link"
              }}</span>
              <div v-if="!showCopied">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                >
                  <g
                    fill="none"
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                  >
                    <path
                      d="M9.396 4.5H8.354c-2.946 0-4.42 0-5.335.879c-.915.878-.915 2.293-.915 5.121v4c0 2.828 0 4.243.915 5.121c.916.879 2.389.879 5.335.879h4.207c2.946 0 4.42 0 5.334-.879c.593-.569.802-1.363.876-2.621"
                    />
                    <path
                      d="M16.167 7V3.854c0-.196.165-.354.368-.354c.098 0 .191.037.26.104l4.732 4.542c.236.227.369.534.369.854s-.133.627-.369.854l-4.732 4.542a.38.38 0 0 1-.26.104a.36.36 0 0 1-.368-.354V11h-3.051c-4.241 0-5.803 3.5-5.803 3.5V12c0-2.761 2.331-5 5.208-5z"
                    />
                  </g>
                </svg>
              </div>
              <i class="mdi mdi-check mr-1 text-green-500" v-else></i>
            </button>
          </div>
        </div>
        <div class="flex flex-col overflow-y-auto">
          <div class="flex flex-col gap-4 mb-6 pt-3 px-4 2xl:px-6">
            <template v-if="isLoading">
              <div v-for="i in 3" :key="i" class="flex items-center gap-2">
                <span
                  class="w-24 h-4 bg-echoes-background-gray rounded animate-pulse"
                ></span>
                <span
                  class="w-32 h-6 bg-echoes-background-gray rounded animate-pulse"
                ></span>
              </div>
            </template>
            <template v-else>
              <!-- Tag Groups -->
              <div class="flex flex-wrap gap-4 max-w-full">
                <div
                  v-for="(tags, category) in nonEmptyTagGroups"
                  :key="category"
                  class="flex flex-wrap items-center gap-2 max-w-full"
                >
                  <span
                    class="text-xs 2xl:text-sm font-medium text-gray-400 break-words"
                    >{{ category }}:</span
                  >
                  <div class="flex flex-wrap items-center gap-2 max-w-full">
                    <span
                      v-for="tag in visibleTags[category]"
                      :key="tag.id"
                      class="whitespace-nowrap px-2 2xl:px-3 text-xs 2xl:text-sm py-0.5 2xl:py-1 flex items-center justify-center font-medium rounded-full"
                      :style="getTagStyle(tag)"
                    >
                      {{ tag.name }}
                    </span>
                    <button
                      v-if="hasMoreTags[category]"
                      @click="toggleTagExpansion(category)"
                      class="px-2 py-1 text-xs font-medium text-violet-400 hover:text-violet-300 transition-colors duration-200"
                    >
                      {{
                        expandedCategories[category]
                          ? "Show less"
                          : `+${
                              nonEmptyTagGroups[category].length -
                              (category === "Use Cases" ? 3 : 5)
                            }`
                      }}
                    </button>
                  </div>
                </div>

        <span v-if="!item?.tags?.length" class="text-sm text-white/80">
            No tags available
        </span>
    </div>
</template>
                    </div>
                    <div class="sticky top-0 bg-echoes-background-black pt-2.5">
                      <Tabs :tabs="tabs" :activeTab="activeTab"
                          @update:activeTab="handleTabChange" />
                    </div>
                    <div class="flex-1">
                      <div v-if="item?.datasets?.[0]?.dataset?.eval?.total_tasks > 0" class="flex-1">
                          <div v-if="activeTab === 'card' && item?.description" class="p-4">
                            <MarkdownRenderer :model-value="item.description" :is-editing="false" :content-only="true" />
                          </div>
                          <div v-if="activeTab === 'task-bundles'" class="h-full p-4">
                            <TaskBundles 
                              :task-bundles="item?.task_bundles || {}" 
                              :eval="item"
                              :loading="isLoadingTaskBundles"
                              :access-restricted="taskBundlesAccessDenied"
                              @auth-required="promptAuth"
                              @unlock="handleTaskBundlesUnlock"
                            />
                          </div>
                          <div v-if="activeTab === 'leaderboards'" class="h-full">
                            <EvalLeaderboards
                              :eval-dataset-id="item.datasets[0].dataset_id"
                              :cached-data="leaderboardData"
                              :has-cached-data="hasLeaderboardData"
                              @leaderboard-data="handleLeaderboardLoaded"
                            />
                          </div>
                          <div v-if="activeTab === 'license'">
                            <div v-if="isLoading" class="p-4">
                              <div class="inline-block w-24 h-4 bg-echoes-background-gray rounded animate-pulse"></div>
                            </div>
                            <div v-else class="p-4">
                              <MarkdownRenderer :model-value="licenseContent" :is-editing="false" :content-only="true" />
                            </div>
                          </div>
                        </div>
                        <div v-else>
                          <div v-if="activeTab === 'card' && item?.description" class="p-4">
                            <MarkdownRenderer :model-value="item.description" :is-editing="false" :content-only="true" />
                          </div>
                          <div v-if="activeTab === 'schema'" class="h-full">
                            <SchemaTable :schema="item?.schema || { fields: [] }" />
                          </div>
                          <div v-if="activeTab === 'license'">
                            <div v-if="isLoading" class="p-4">
                                <div class="inline-block w-24 h-4 bg-echoes-background-gray rounded animate-pulse"></div>
                            </div>
                            <div v-else class="p-4">
                                <MarkdownRenderer :model-value="licenseContent" :is-editing="false" :content-only="true" />
                            </div>
                          </div>
                        </div>
                    </div>
                </div>
                
            </section>

      <!-- Right Section -->
      <section
        class="hidden lg:flex flex-col rounded-[28px] rounded-b-none bg-gradient-to-t from-transparent to-echoes-background-gray border border-b-0 border-echoes-border-gray p-3 h-full overflow-hidden transition-all duration-300"
        :class="chatStarted ? 'w-[30%]' : 'w-[20%]'"
      >
        <!-- Loading State -->
        <div
          v-if="isRightSectionLoading"
          class="w-full h-full bg-echoes-background-black/50 rounded-[21px] animate-pulse flex items-center justify-center"
        >
          <div class="flex flex-col items-center gap-3">
            <div class="animate-spin rounded-full h-12 w-12 border-3 border-echoes-background-gray border-t-echoes-primary"></div>
            <p class="text-sm text-white/60">Loading...</p>
          </div>
        </div>

        
        <div
          v-else
          class="flex flex-col text-start h-full min-h-0"
        >

          <template v-if="item?.is_open_source">
            <div
              v-if="!isDatasetOwner && !(isRFPOwner && !datasets)"
              class="flex flex-col gap-2 flex-shrink-0"
            >
              <label for="github-url" class="text-xs text-white/80 font-semibold uppercase pl-1">Eval</label>
              <div class="w-full border border-echoes-border-gray p-3 bg-echoes-background-black rounded-[17px] flex-shrink-0">
                <a
                  v-if="hasValidGithubUrl"
                  id="github-url"
                  :href="itemGithubUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center justify-center gap-2 w-full font-semibold px-4 py-2.5 2xl:py-3 rounded-[13px] transition-all duration-500 text-sm 2xl:text-base bg-gradient-to-r from-[#081928] to-[#075985] hover:from-[#075985] hover:to-[#0a7ba8] text-white shadow-sm"
                >
                  View on Github
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
                <button
                  v-else
                  disabled
                  class="flex items-center justify-center gap-2 w-full font-semibold px-4 py-2.5 2xl:py-3 rounded-[13px] text-sm 2xl:text-base bg-gradient-to-r from-[#081928] to-[#075985] text-white/60 shadow-sm cursor-not-allowed opacity-60"
                >
                  View on Github
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </button>
              </div>
            </div>
            <div
              v-if="isDatasetOwner || (isRFPOwner && !datasets)"
              class="border border-echoes-border-gray p-3.5 bg-echoes-background-black/50 rounded-[17px] flex-shrink-0 mt-3"
            >
              <div v-if="isDatasetOwner && item?.applicant" class="space-y-3">
                <p class="text-xs 2xl:text-sm text-white/80 text-center">
                  You have already submitted a dataset for this RFP
                </p>
                <button
                  @click="goToResponsesRFP"
                  class="flex items-center justify-center w-full font-semibold px-4 py-2.5 2xl:py-3 rounded-[13px] transition-all duration-500 text-sm 2xl:text-base from-violet-500 to-violet-800 text-white hover:from-violet-600 hover:via-violet-500 hover:to-violet-900 cursor-pointer bg-gradient-to-r"
                >
                  View Response
                </button>
              </div>
              <div v-else class="space-y-3">
                <p class="text-xs 2xl:text-sm text-white/80 text-center">
                  Manage your {{ isRFPOwner ? " RFP" : "listing" }}
                </p>
                <button
                  @click="goToEditListing"
                  class="flex items-center justify-center w-full font-semibold px-4 py-2.5 2xl:py-3 rounded-[13px] transition-all duration-500 text-sm 2xl:text-base from-violet-500 to-violet-800 text-white hover:from-violet-600 hover:via-violet-500 hover:to-violet-900 cursor-pointer bg-gradient-to-r"
                >
                  Edit {{ isRFPOwner ? "RFP" : "Listing" }}
                </button>
              </div>
            </div>
          </template>

          <!-- RFP Section -->
          <template v-else-if="item && !datasets && !isRFPOwner">
            <div v-if="!isExpired" class="flex-shrink-0">
              <button
                @click="handleSubmitProposalClick"
                :disabled="isAnyModalActive"
                :class="[
                  canSubmitProposal || !isLoggedIn()
                  ? 'bg-gradient-to-r text-sm 2xl:text-base from-green-500 to-sky-800 text-white hover:from-green-600 hover:via-green-500 hover:to-sky-700 cursor-pointer'
                  : 'bg-gray-300 cursor-not-allowed text-gray-600',
                (isAnyModalActive) ? 'opacity-50 cursor-not-allowed' : '',
              ]"
              class="w-full font-semibold px-4 py-2.5 2xl:py-3 rounded-[13px] transition-all duration-500"
              >
                Submit Proposal
              </button>
            </div>
            <div v-else-if="isExpired" class="flex-shrink-0">
              <p class="text-center text-gray-500 dark:text-gray-400">
                This RFP has expired.
              </p>
            </div>
            <div v-else class="flex-shrink-0">
              <p class="text-center text-gray-500 dark:text-gray-400">
                You need to be a dataset owner to respond to RFPs
              </p>
            </div>
          </template>

          <!-- Owner Section -->
          <template v-else-if="isDatasetOwner || (isRFPOwner && !datasets)">
            <div
              class="border border-echoes-border-gray p-3.5 bg-echoes-background-black/50 rounded-[17px] flex-shrink-0"
            >
              <div v-if="isDatasetOwner && item.applicant" class="space-y-3">
                <p class="text-xs 2xl:text-sm text-white/80 text-center">
                  You have already submitted a dataset for this RFP
                </p>
                <button
                  @click="goToResponsesRFP"
                  class="flex items-center justify-center w-full font-semibold px-4 py-2.5 2xl:py-3 rounded-[13px] transition-all duration-500 text-sm 2xl:text-base from-violet-500 to-violet-800 text-white hover:from-violet-600 hover:via-violet-500 hover:to-violet-900 cursor-pointer bg-gradient-to-r"
                >
                  View Response
                </button>
              </div>
              <div v-else class="space-y-3">
                <p class="text-xs 2xl:text-sm text-white/80 text-center">
                  Manage your {{ isRFPOwner ? " RFP" : "listing" }}
                </p>
                <button
                  @click="goToEditListing"
                  class="flex items-center justify-center w-full font-semibold px-4 py-2.5 2xl:py-3 rounded-[13px] transition-all duration-500 text-sm 2xl:text-base from-violet-500 to-violet-800 text-white hover:from-violet-600 hover:via-violet-500 hover:to-violet-900 cursor-pointer bg-gradient-to-r"
                >
                  Edit {{ isRFPOwner ? "RFP" : "Listing" }}
                </button>
              </div>
            </div>

            <div v-if="hasFlag('asymmetry0-chat') && !qaError" class="py-3 mt-3 bg-echoes-background-black rounded-t-[17px] border border-echoes-border-gray border-b-0 flex items-center justify-center flex-shrink-0">
                <div class="relative gap-2 flex items-center cursor-pointer group">
                  <h3
                    class="text-sm text-center text-white cursor-pointer uppercase flex items-center justify-center gap-2"
                  >
                    Asymmetry0
                  </h3>
                  <i
                    class="mdi mdi-information-outline text-echoes-primary hover:text-echoes-primary/80 transition-colors duration-200"
                  ></i>
                  <span class="absolute border border-echoes-border-light left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 text-xs font-semibold text-white/80 bg-echoes-background-gray rounded-lg whitespace-normal w-max max-w-[250px] invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-200 z-[99999]">Asymmetry0 is an agent swarm that analyzes a dataset's use cases, key value propositions, and quality.</span>
                </div>
            </div>

            <div
                v-if="hasFlag('asymmetry0-chat') && !qaError"
                class="bg-echoes-background-black rounded-b-[17px] border border-echoes-border-gray flex flex-col overflow-hidden"
                :class="chatStarted ? 'flex-1' : ''"
                >

              <div
                v-if="chatStarted"
                class="flex flex-col flex-1 min-h-0 overflow-hidden"
              >
                <!-- Messages Container -->
                <div
                  ref="messagesContainer"
                  class="flex-1 overflow-y-auto px-3 py-3 space-y-3"
                >
                  <div
                    v-if="messages.length === 0"
                    class="flex items-center justify-center py-8"
                  >
                    <div
                      class="animate-spin rounded-full h-8 w-8 border-2 border-white/20 border-t-white/60"
                    ></div>
                  </div>

                  <!-- Messages -->
                  <div
                    v-for="(message, index) in messages"
                    :key="index"
                    class="mb-3"
                  >
                    <div
                      v-if="message.type === 'user'"
                      class="flex justify-end"
                    >
                      <div
                        class="max-w-[80%] bg-echoes-background-gray rounded-lg px-3.5 py-3 text-white text-sm font-medium leading-tight break-words"
                      >
                        <MarkdownRenderer :model-value="message.content" class="w-full rounded-lg text-white text-xs font-medium leading-tight" :content-only="true"/>
                      </div>
                    </div>

                    <div
                      v-else
                      class="text-white/90 text-sm leading-relaxed break-words"
                    >
                      <div
                        v-if="message.loading"
                        class="flex items-center gap-2 text-white/60"
                      >
                        <span>Thinking</span>
                        <div class="flex items-center gap-1">
                          <div
                            class="w-1 h-1 bg-echoes-primary/60 rounded-full animate-pulse-dot"
                            style="animation-delay: 0ms"
                          ></div>
                          <div
                            class="w-1 h-1 bg-echoes-primary/80 rounded-full animate-pulse-dot"
                            style="animation-delay: 200ms"
                          ></div>
                          <div
                            class="w-1 h-1 bg-echoes-primary rounded-full animate-pulse-dot"
                            style="animation-delay: 400ms"
                          ></div>
                        </div>
                      </div>
                      <MarkdownRenderer v-else :model-value="message.content" class="w-full rounded-lg text-white text-xs font-medium leading-tight" :content-only="true"/>
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-else-if="qaResponseReceived && !qaError"
                class="p-3 space-y-3 overflow-y-auto scrollbar-thin scrollbar-thumb-echoes-border-gray scrollbar-track-transparent hover:scrollbar-thumb-echoes-border-light"
              >
                <div
                  v-for="(question, index) in conversationStarters"
                  :key="index"
                >
                  <button
                    @click="openConversation(question)"
                    class="w-full py-4 px-3 rounded-[12px] bg-echoes-background-gray hover:bg-echoes-background-gray/80 text-white cursor-pointer"
                  >
                    <div class="flex flex-col gap-2">
                      <div class="flex justify-between items-center gap-2">
                        <p class="text-sm text-left text-white flex-1">
                          {{ question }}
                        </p>
                        <div
                          class="flex items-center justify-center text-xl h-7 w-7 flex-shrink-0"
                        >
                          <i class="uil uil-arrow-circle-right"></i>
                        </div>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              <div v-if="isAdmin" class="p-3 w-full flex-shrink-0 border-t border-echoes-border-gray bg-echoes-background-black">
                <div class="w-full flex items-center relative">
                  <textarea
                    ref="chatInputRef"
                    v-model="customQuestion"
                    placeholder="Ask anything..."
                    :disabled="isChatLoading"
                    class="w-full flex items-center resize-none bg-echoes-background-black ring-1 ring-echoes-border-gray rounded-[10px] pl-3 pr-[64px] py-2 text-sm text-white/80 placeholder-white/50 focus:outline-none focus:ring-echoes-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    @keydown="handleKeyDown"
                    @input="handleInput"
                    rows="1"
                    style="height: 40px"
                  ></textarea>
                  <button
                    @click="askQuestion"
                    :disabled="!customQuestion.trim() || isChatLoading"
                    class="absolute right-0 mr-1 bottom-1 px-3 py-1.5 rounded-lg text-white font-medium text-xs transition-colors duration-200 bg-echoes-border-light hover:bg-echoes-border-light/80 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-echoes-border-light flex items-center justify-center h-8"
                  >
                    <svg
                      v-if="isChatLoading"
                      class="animate-spin w-3 h-3"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      ></circle>
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span v-else>{{ chatStarted ? "Send" : "Ask" }}</span>
                  </button>
                </div>
              </div>
            </div>
          </template>

          <!-- Logged In Section -->
          <template v-else>
            <div class="flex flex-col gap-2">

              <!-- Eval Run Section -->
                <div v-if="item?.eval_price > 0" class="mt-2">
                  <div
                    v-if="showCreatorBanner"
                    class="mb-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 flex flex-col gap-2"
                  >
                    <p class="text-white font-semibold text-sm">Want to build your own benchmark?</p>
                    <p class="text-white/70 text-xs">Buy Benchmark credits from Explore to create and run evals.</p>
                  </div>
                  <label class="text-xs text-white/80 font-semibold uppercase pl-1" for="eval-run-modal">Eval</label>
                  <div class="w-full flex-1 border border-echoes-border-gray p-3 bg-echoes-background-black rounded-[17px] space-y-3 flex-shrink-0">
                    <div class="flex gap-3 align-start">
                      <button
                        id="eval-run-modal"
                        @click="handleRunEvalClick"
                        :disabled="(isLoggedIn() && isAnyModalActive) || isExpired"
                        :class="[
                          (isLoggedIn() && isAnyModalActive) || isExpired
                            ? 'bg-gray-300 cursor-not-allowed text-gray-600 opacity-50'
                            : 'bg-gradient-to-r text-sm 2xl:text-base from-green-500 to-sky-800 text-white hover:from-green-600 hover:via-green-500 hover:to-sky-700 cursor-pointer',
                        ]"
                        class="flex items-center gap-2 font-semibold px-4 py-2.5 2xl:py-3 rounded-[13px] transition-all duration-500"
                      >
                      <svg width="12" height="12" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M32.0889 0C32.6783 0 33.2435 0.234127 33.6602 0.650874L39.3491 6.33976C40.217 7.2076 40.217 8.61463 39.3491 9.48246L33.7649 15.0667L24.9333 6.23508L30.5175 0.650874C30.9343 0.234126 31.4995 0 32.0889 0ZM21.7906 9.37778L0.650874 30.5175C0.234126 30.9343 0 31.4995 0 32.0889C0 32.6783 0.234126 33.2435 0.650874 33.6602L6.33976 39.3491C7.2076 40.217 8.61463 40.217 9.48246 39.3491L30.6222 18.2094L21.7906 9.37778Z" fill="currentColor"></path>
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M6.66667 6.66667C7.89397 6.66667 8.88889 7.66159 8.88889 8.88889V11.1111H11.1111C12.3384 11.1111 13.3333 12.106 13.3333 13.3333C13.3333 14.5606 12.3384 15.5556 11.1111 15.5556H8.88889V17.7778C8.88889 19.0051 7.89397 20 6.66667 20C5.43937 20 4.44444 19.0051 4.44444 17.7778V15.5556H2.22222C0.994923 15.5556 0 14.5606 0 13.3333C0 12.106 0.994923 11.1111 2.22222 11.1111H4.44444V8.88889C4.44444 7.66159 5.43937 6.66667 6.66667 6.66667Z" fill="currentColor"></path>
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M26.6667 26.6667C27.894 26.6667 28.8889 27.6616 28.8889 28.8889V31.1111H31.1111C32.3384 31.1111 33.3333 32.106 33.3333 33.3333C33.3333 34.5606 32.3384 35.5556 31.1111 35.5556H28.8889V37.7778C28.8889 39.0051 27.894 40 26.6667 40C25.4394 40 24.4444 39.0051 24.4444 37.7778V35.5556H22.2222C20.9949 35.5556 20 34.5606 20 33.3333C20 32.106 20.9949 31.1111 22.2222 31.1111H24.4444V28.8889C24.4444 27.6616 25.4394 26.6667 26.6667 26.6667Z" fill="currentColor"></path>
                          <path d="M35.5556 22.2222H31.1111V26.6667H35.5556V22.2222Z" fill="currentColor"></path>
                          <path d="M22.2222 0H17.7778V4.44444H22.2222V0Z" fill="currentColor"></path>
                          <path d="M17.7778 4.44444H13.3333V8.88889H17.7778V4.44444Z" fill="currentColor"></path>
                          <path d="M13.3333 0H8.88889V4.44444H13.3333V0Z" fill="currentColor"></path>
                          <path d="M40 17.7778H35.5556V22.2222H40V17.7778Z" fill="currentColor"></path>
                          <path d="M40 26.6667H35.5556V31.1111H40V26.6667Z" fill="currentColor"></path>
                        </svg>
                        <span>Run Eval</span>
                      </button>
                      <div class="flex flex-col gap-2 flex-1">
                        <p class="text-xs text-white uppercase">Price:</p>
                        <p class="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                          {{ formatPrice(item?.eval_price || 0) }}/Run
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

              <!-- Core Dataset Section -->
              <div v-if="item?.price > 0 || item?.minimum_bid > 0" class="mt-2">
                <label class="text-xs text-white/80 font-semibold uppercase pl-1" for="core-dataset-modal">Core Dataset</label>
                <div class="border border-echoes-border-gray p-3 bg-echoes-background-black rounded-[17px] flex-shrink-0">
                    
                    <!-- Buy and Bid Sections in Same Row -->
                    <div class="flex gap-3 align-start" :class="gridColumns">
                      <!-- Buy Section -->
                      <div
                        v-if="item?.price > 0 && item?.datasets?.length"
                        class="w-full flex flex-col flex-1 gap-3"
                      >
                      <div class="flex flex-col gap-1 justify-start items-start">
                        <p class="text-xs text-white uppercase">Price:</p>
                        <p class="text-lg font-bold text-emerald-600 dark:text-emerald-400 break-words">
                          {{ formatPrice(item?.price) }}
                        </p>
                      </div>
                        <button
                          id="core-dataset-modal"
                          @click="isLoggedIn() ? (datasets?.dataset?.external_source?.id ? (showRequestAccessDatasetModal = true) : toggleBuyNowModal(item)) : handleBuyNowClick(item)"
                          :disabled="isLoggedIn() && isAnyModalActive"
                          :class="[
                            !buyNowClicked
                              ? 'bg-gradient-to-r text-sm 2xl:text-base from-green-500 to-sky-800 text-white hover:from-green-600 hover:via-green-500 hover:to-sky-700 cursor-pointer'
                              : 'bg-gray-300 cursor-not-allowed text-gray-600',
                            (isLoggedIn() && isAnyModalActive) ? 'opacity-50 cursor-not-allowed' : '',
                          ]"
                          class="h-[44px] flex items-center justify-center text-size-[13px] 2xl:text-sm 2xl:text-base font-semibold px-4 py-2.5 2xl:py-3 rounded-[13px] transition-all duration-500"
                        >
                          <i class="mdi mdi-lightning-bolt mr-1"></i>Buy Now
                        </button>
                      </div>
      
                      <!-- Bid Section -->
                      <div
                        v-if="item?.minimum_bid > 0"
                        class="w-full flex flex-col flex-1 gap-3"
                      >
                      <div class="flex flex-col gap-1 justify-start items-start">
                        <p class="text-xs text-white uppercase">Minimum Bid:</p>
                        <p class="text-lg font-bold text-emerald-600 dark:text-emerald-400 break-words">
                          {{ formatPrice(item?.minimum_bid) }}
                        </p>
                      </div>
                        <button
                          id="core-dataset-modal"
                          @click="handleBidClick(item)"
                          :disabled="isLoggedIn() && isAnyModalActive"
                          :class="[
                            !bidClicked
                              ? 'bg-gradient-to-r text-sm 2xl:text-base from-blue-500 to-violet-800 text-white hover:from-blue-600 hover:via-blue-500 hover:to-violet-700 cursor-pointer'
                              : 'bg-gray-300 cursor-not-allowed text-gray-600',
                            (isLoggedIn() && isAnyModalActive) ? 'opacity-50 cursor-not-allowed' : '',
                          ]"
                          class="h-[44px] flex items-center justify-center text-size-[13px] 2xl:text-sm 2xl:text-base font-semibold px-4 py-2.5 2xl:py-3 rounded-[13px] transition-all duration-500"
                        >
                          <i class="mdi mdi-gavel mr-1"></i> Place Bid
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
            </div>

            <!-- RFP Section -->
            <template v-if="item && !datasets">
              <div v-if="hasPermission('datasets') && !isExpired" class="flex-shrink-0">
                <button
                  v-if="canRespondToRfp"
                  @click="toggleRFPRespondModal(item)"
                  :disabled="isAnyModalActive"
                  :class="[
                    !rfpRespondClicked
                      ? 'bg-gradient-to-r text-sm 2xl:text-base  cursor-pointer'
                      : 'bg-gray-300 cursor-not-allowed text-gray-600',
                    isAnyModalActive ? 'opacity-50 cursor-not-allowed' : '',
                  ]"
                  class="w-full font-semibold px-4 py-2.5 2xl:py-3 rounded-[13px] transition-all duration-500"
                >
                  Respond to RFP
                </button>
                <button
                  v-else-if="canSubmitProposal"
                  @click="handleSubmitProposalClick"
                  :disabled="isLoggedIn() && isAnyModalActive"
                  :class="[
                    !proposalClicked
                      ? 'bg-gradient-to-r text-sm 2xl:text-base from-green-500 to-sky-800 text-white hover:from-green-600 hover:via-green-500 hover:to-sky-700 cursor-pointer'
                      : 'bg-gray-300 cursor-not-allowed text-gray-600',
                    (isLoggedIn() && isAnyModalActive) ? 'opacity-50 cursor-not-allowed' : '',
                  ]"
                  class="w-full font-semibold px-4 py-2.5 2xl:py-3 rounded-[13px] transition-all duration-500"
                >
                  Submit Proposal
                </button>

                <div
                  v-else-if="viewProposal"
                  class="flex flex-col gap-2 text-center font-semibold border border-echoes-border-gray p-3.5 bg-echoes-background-black/50 rounded-[17px] space-y-1"
                >
                  <div class="text-xs text-white/80">
                    You have already submitted a proposal
                  </div>
                  <button
                    @click="goToProposal"
                    class="flex items-center justify-center w-full font-semibold px-4 py-2.5 2xl:py-3 rounded-[13px] transition-all duration-500 text-sm 2xl:text-base from-violet-500 to-violet-800 text-white hover:from-violet-600 hover:via-violet-500 hover:to-violet-900 cursor-pointer bg-gradient-to-r"
                  >
                    View proposal
                  </button>
                </div>
              </div>
              <div v-else-if="isExpired" class="flex-shrink-0">
                <p class="text-center text-gray-500 dark:text-gray-400">
                  This RFP has expired.
                </p>
              </div>
              <div v-else class="flex-shrink-0">
                <p class="text-center text-gray-500 dark:text-gray-400">
                  You need to be a dataset owner to respond to RFPs
                </p>
              </div>
            </template>
          
              <div  v-if="hasFlag('asymmetry0-chat') && !qaError" class="py-3 mt-3 bg-echoes-background-black rounded-t-[17px] border border-echoes-border-gray border-b-0 flex items-center justify-center flex-shrink-0">
                <div class="relative gap-2 flex items-center cursor-pointer group">
                  <h3
                    class="text-sm text-center text-white cursor-pointer uppercase flex items-center justify-center gap-2"
                  >
                    Asymmetry0
                  </h3>
                  <i
                    class="mdi mdi-information-outline text-echoes-primary hover:text-echoes-primary/80 transition-colors duration-200"
                  ></i>
                  <span class="absolute border border-echoes-border-light left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 text-xs font-semibold text-white/80 bg-echoes-background-gray rounded-lg whitespace-normal w-max max-w-[250px] invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-200 z-[99999]">Asymmetry0 is an agent swarm that analyzes a dataset's use cases, key value propositions, and quality.</span>
                </div>
              </div>

              <div
              v-if="hasFlag('asymmetry0-chat') && !qaError"
              class="bg-echoes-background-black rounded-b-[17px] border border-echoes-border-gray flex flex-col overflow-hidden"
              :class="chatStarted ? 'flex-1' : ''"
            >

              <div
                v-if="chatStarted"
                class="flex flex-col flex-1 min-h-0 overflow-hidden"
              >
                <!-- Messages Container -->
                <div
                  ref="messagesContainer"
                  class="flex-1 overflow-y-auto px-3 py-3 space-y-3"
                >
                  <div
                    v-if="messages.length === 0"
                    class="flex items-center justify-center py-8"
                  >
                    <div
                      class="animate-spin rounded-full h-8 w-8 border-2 border-white/20 border-t-white/60"
                    ></div>
                  </div>

                  <!-- Messages -->
                  <div
                    v-for="(message, index) in messages"
                    :key="index"
                    class="mb-3"
                  >
                    <div
                      v-if="message.type === 'user'"
                      class="flex justify-end"
                    >
                      <div
                        class="max-w-[80%] bg-echoes-background-gray rounded-lg px-3.5 py-3 text-white text-sm font-medium leading-tight break-words"
                      >
                        <MarkdownRenderer :model-value="message.content" class="w-full rounded-lg text-white text-xs font-medium leading-tight" :content-only="true"/>
                      </div>
                    </div>

                    <div
                      v-else
                      class="text-white/90 text-sm leading-relaxed break-words"
                    >
                      <div
                        v-if="message.loading"
                        class="flex items-center gap-2 text-white/60"
                      >
                        <span>Thinking</span>
                        <div class="flex items-center gap-1">
                          <div
                            class="w-1 h-1 bg-echoes-primary/60 rounded-full animate-pulse-dot"
                            style="animation-delay: 0ms"
                          ></div>
                          <div
                            class="w-1 h-1 bg-echoes-primary/80 rounded-full animate-pulse-dot"
                            style="animation-delay: 200ms"
                          ></div>
                          <div
                            class="w-1 h-1 bg-echoes-primary rounded-full animate-pulse-dot"
                            style="animation-delay: 400ms"
                          ></div>
                        </div>
                      </div>
                      <MarkdownRenderer v-else :model-value="message.content" class="w-full rounded-lg text-white text-xs font-medium leading-tight" :content-only="true"/>
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-else-if="qaResponseReceived && !qaError"
                class="p-3 space-y-3 overflow-y-auto scrollbar-thin scrollbar-thumb-echoes-border-gray scrollbar-track-transparent hover:scrollbar-thumb-echoes-border-light"
              >
                <div
                  v-for="(question, index) in conversationStarters"
                  :key="index"
                >
                  <button
                    @click="openConversation(question)"
                    class="w-full py-4 px-3 rounded-[12px] bg-echoes-background-gray hover:bg-echoes-background-gray/80 text-white cursor-pointer"
                  >
                    <div class="flex flex-col gap-2">
                      <div class="flex justify-between items-center gap-2">
                        <p class="text-sm text-left text-white flex-1">
                          {{ question }}
                        </p>
                        <div
                          class="flex items-center justify-center text-xl h-7 w-7 flex-shrink-0"
                        >
                          <i class="uil uil-arrow-circle-right"></i>
                        </div>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              <div v-if="isAdmin" class="p-3 w-full flex-shrink-0 border-t border-echoes-border-gray bg-echoes-background-black">
                <div class="w-full flex items-center relative">
                  <textarea
                    ref="chatInputRef"
                    v-model="customQuestion"
                    placeholder="Ask anything..."
                    :disabled="isChatLoading"
                    class="w-full flex items-center resize-none bg-echoes-background-black ring-1 ring-echoes-border-gray rounded-[10px] pl-3 pr-[64px] py-2 text-sm text-white/80 placeholder-white/50 focus:outline-none focus:ring-echoes-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    @keydown="handleKeyDown"
                    @input="handleInput"
                    rows="1"
                    style="height: 40px"
                  ></textarea>
                  <button
                    @click="askQuestion"
                    :disabled="!customQuestion.trim() || isChatLoading"
                    class="absolute right-0 mr-1 bottom-1 px-3 py-1.5 rounded-lg text-white font-medium text-xs transition-colors duration-200 bg-echoes-border-light hover:bg-echoes-border-light/80 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-echoes-border-light flex items-center justify-center h-8"
                  >
                    <svg
                      v-if="isChatLoading"
                      class="animate-spin w-3 h-3"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      ></circle>
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span v-else>{{ chatStarted ? "Send" : "Ask" }}</span>
                  </button>
                </div>
              </div>
            </div>

            
            
          </template>
        </div>
      </section>
    </div>

    <BidModal
      v-if="isBidActive"
      :isOpen="isBidActive"
      :bidActive="bidActive"
      @close="isBidActive = false"
    />
    <RFPRespondModal
      v-if="isRFPRespondActive"
      :isActive="isRFPRespondActive"
      :rfpRespond="rfpRespond"
      @close="isRFPRespondActive = false"
    />
    <CheckoutModal
      v-model:showCheckoutModal="showCheckoutModal"
      :buyActive="buyActive"
      v-if="showCheckoutModal"
    />
    <RequestAccessDatasetModal
      v-if="showRequestAccessDatasetModal"
      :show="showRequestAccessDatasetModal"
      :listing="item"
      @close="showRequestAccessDatasetModal = false"
    />
    <SubmitProposalModal
      v-if="submitProposalModal"
      :show="submitProposalModal"
      :listing="item"
      @close="submitProposalModal = false"
      @confirm="redirectToNewProposal"
    />
    <RunEvalModal
      v-if="showRunEvalModal"
      :show-modal="showRunEvalModal"
      :eval-listing="item"
      :task-bundles="item?.task_bundles"
      @update:showModal="showRunEvalModal = $event"
      @eval-completed="handleEvalCompleted($event)"
    />
    <AuthPromptModal
      v-if="showAuthPromptModal"
      :show-modal="showAuthPromptModal"
      :action-type="authPromptAction"
      @update:showModal="showAuthPromptModal = $event"
      @login="handleAuthLogin"
      @register="handleAuthRegister"
    />
    <ProfilePopup
      :is-open="showProfilePopup"
      :profile="profileData"
      @close="showProfilePopup = false"
    />

  </main>
</template>
<style scoped lang="postcss">
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

body {
  overflow-x: hidden;
}
.tooltip {
  @apply absolute left-1/2 border border-echoes-border-light -translate-x-1/2 bottom-full px-3 py-2 text-xs font-semibold text-white/80 bg-echoes-background-gray rounded-lg whitespace-normal w-max max-w-[300px];
  visibility: hidden;
  opacity: 0;
  transition: all 0.2s ease-in-out;
  z-index: 99999;
  margin-bottom: 2px;
overflow: hidden;
}
button:hover .tooltip {
  visibility: visible;
  opacity: 1;
}



@keyframes pulse-dot {
  0%,
  100% {
    transform: scale(0.6);
    opacity: 0.4;
  }
  50% {
    transform: scale(1.3);
    opacity: 1;
  }
}

.animate-pulse-dot {
  animation: pulse-dot 1.5s infinite ease-in-out;
}

</style>